import { ReactTFC } from "../../../utils/fp/missing-ts-parts";

import { css } from "@emotion/react";

import React from "react";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { CSS_SPACER_SIZES } from "../../../styles/styles-css-variables";

import { SwarovskiOptikLocaleFrontend, type TranslatedValue } from "@swo/storefront";
import { NoImageSvg } from "../../../assets";
import { ContentfulImage } from "../../../components/_shared-components/image-related/ContentfulImage";
import { SwarovskiLink } from "../../../components/_shared-components/ui-elements/SwarovskiLink";
import { T } from "../../../components/T";
import { formatStartingPrice } from "../../../domain/swarovski-optik/products/price/format-price";
import { CSS_MQ } from "../../../styles/breakpoints";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";
import { StateT } from "../product-comparison-page-component";

export const PCPProductTeaser: ReactTFC<{
  product: StateT["selectedProducts"][0];
  buyCallToActionLabel: TranslatedValue;
  fromPriceLabelTranslation: TranslatedValue;
  lowestPriceLast30DaysTranslation: TranslatedValue;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
}> = props => {
  const { image: img, startingPrice, discountedStartingPrice, lowestPrice30Days, discountPercentage } = props.product.product;

  return (
    <ProductTeaserElement
      image={
        img
          ? {
              url: img.url,
              alt: img.description || img.title,
              sys: { id: img.sys.id },
            }
          : undefined
      }
      startingPriceLabel={
        startingPrice
          ? formatStartingPrice(
              { startingPrice, discountedStartingPrice, lowestPrice30Days, discountPercentage },
              props.fromPriceLabelTranslation,
              props.lowestPriceLast30DaysTranslation,
              props.localeForNumberFormat,
            )
          : undefined
      }
      ctaButton={{ link: props.product.pdpLink, label: props.buyCallToActionLabel }}
    />
  );
};

export const ProductTeaserImageElement: ReactTFC<{
  image: { url: string; alt: string | undefined; sys: { id: string } } | undefined;
  link: string;
}> = props => {
  return (
    <div css={imageWrapperStyles}>
      {props.image ? (
        <SwarovskiLink
          isInternal={true}
          link={props.link}
          scrollToTop={true}
          attachStyles={css`
            display: block;
            width: 100%;
            height: 100%;
          `}
        >
          <ContentfulImage
            assetId={props.image.sys.id}
            contentfulLink={{
              url: props.image.url,
              noTransparentBackground: false,
              requestedWidth: 150,
            }}
            higherDPI={[2, 3]}
            alt={props.image.alt || ""}
            css={css`
              max-width: 100%;
              max-height: 100%;
            `}
            attachPictureStyles={css`
              display: block;
              width: 100%;
              height: 100%;
            `}
          />
        </SwarovskiLink>
      ) : (
        <NoImageSvg
          css={css`
            max-width: 100%;
            max-height: 100%;
          `}
          strokeWidth={1}
        />
      )}
    </div>
  );
};

export const ProductTeaserElement: ReactTFC<{
  image: { url: string; alt: string | undefined; sys: { id: string } } | undefined;
  startingPriceLabel: React.ReactElement | undefined;
  ctaButton: { link: string; label: TranslatedValue };
  children?: React.ReactNode;
}> = props => {
  return (
    <div css={wrapperStyles}>
      <ProductTeaserElementContents {...props} />
    </div>
  );
};

export const ProductTeaserElementContents: ReactTFC<{
  image: { url: string; alt: string | undefined; sys: { id: string } } | undefined;
  startingPriceLabel: React.ReactElement | undefined;
  ctaButton: { link: string; label: TranslatedValue };
  children?: React.ReactNode;
}> = props => {
  return (
    <React.Fragment>
      <ProductTeaserImageElement image={props.image} link={props.ctaButton.link} />

      {props.children}

      {props.startingPriceLabel && <div css={startingPriceStyles}>{props.startingPriceLabel}</div>}

      <span css={ctaStyles}>
        <SwarovskiButton
          as="a"
          isInternal={true}
          link={props.ctaButton.link}
          scrollToTop={true}
          variant={"green"}
          size={"small"}
        >
          <T>{props.ctaButton.label}</T>
        </SwarovskiButton>
      </span>
    </React.Fragment>
  );
};

const wrapperStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const imageWrapperStyles = css`
  height: 80px;
  ${CSS_MQ.atLeastMd} {
    height: 120px;
  }

  display: flex;
  align-items: center;
`;

const startingPriceStyles = [
  FONTS.SwarovskiFutura500,
  fontColor.black,
  fontSize.x25,
  css`
    margin-top: ${CSS_SPACER_SIZES.md};
    margin-bottom: 20px;
    text-align: center;
  `,
];

const ctaStyles = css`
  margin-bottom: ${CSS_SPACER_SIZES.md};
`;
